import { Capacitor } from '@capacitor/core';

/**
 * Configuration des URLs d'API pour le backoffice
 * Routes les requêtes vers la bonne API (principale ou secondaire)
 */

// URLs des APIs en production (web) — principale + secondaire actuelles
const API_PRIMARY_PROD = 'https://w6khpitahj.execute-api.eu-west-3.amazonaws.com/api';
const API_SECONDARY_PROD = 'https://137jrhqqx9.execute-api.eu-west-3.amazonaws.com/api';

// APIs dédiées aux apps mobiles (stage mobile_v1) — utilisées uniquement en natif (iOS/Android)
const API_PRIMARY_MOBILE_V1 = 'https://o1s139ji9g.execute-api.eu-west-3.amazonaws.com/api';
const API_SECONDARY_MOBILE_V1 = 'https://ruc3rcd1wc.execute-api.eu-west-3.amazonaws.com/api';

// URLs locales
const API_PRIMARY_LOCAL = 'http://localhost:8000';
const API_SECONDARY_LOCAL = 'http://localhost:8001';

/**
 * Support #424 : stats V2 doivent frapper la même API que le web, sinon mobile_v1
 * (souvent en retard de déploiement) renvoie des totaux différents à période égale.
 */
const NATIVE_FORCE_WEB_PROD_PATTERNS: RegExp[] = [
  /^\/pros\/[^/]+\/stats\/v2(?:\/|$)/,
  // #672 — native app must not hit stale mobile_v1 for profile/gallery uploads
  /^\/upload-url$/,
  /^\/upload-image$/,
  // #690 — SUPER PDP env absentes sur mobile_v1 → forcer API web prod
  /^\/efacturation(?:\/|$)/,
  // Offre ACACED : endpoints récents, absents des stages mobile_v1 en retard de déploiement
  /^\/professionals\/[^/]+\/acaced(?:\/|$)/,
  // #1387 — GET /follow-engine/animals (#1355) : mobile_v1 secondaire souvent sans la route → 403 tableau vide
  /^\/follow-engine(?:\/|$)/,
];

// Routes qui doivent utiliser l'API secondaire
// Ces routes correspondent aux blueprints enregistrés dans API_Secondary/app.py
const SECONDARY_API_ROUTES = [
  '/forfaits',
  '/forfait',
  '/get-valid-forfaits',
  '/add-service-to-forfait',
  '/create-forfait',
  '/delete-forfait',
  '/archive-forfait',
  '/reactivate-forfait',
  '/purchase-forfait',
  '/confirm-forfait-purchase',
  '/update-forfait',
  '/update-forfait-order',
  '/purchase-credits',
  '/accept-cgv',
  '/accept-cgv-forfait',
  '/accept-cgv-credits',
  '/gift-credits',
  '/gift-vouchers',
  '/public/gift-vouchers',
  '/withdraw-credits',
  '/create-subscription-plan',
  '/client-credits',
  '/subscribe',
  '/user-subscription',
  '/sell-credits-pro',
  '/forfaits-for-booking',
  '/update-service-credit-price',
  '/check-client-email',
  '/sell-forfait-offline',
  '/emergency-fix-forfait',
  '/public/calculate-forfait-travel-fees',
  '/calculate-forfait-home-delivery',
  '/calculate-forfait-travel-fees',
  '/sold-forfaits',
  // Plan de paiement forfait hors ligne (forfait_controller → API secondaire uniquement, pas sur app.py hors pytest)
  '/user-forfaits',
  '/user-credits',
  '/user-pro-credits',
  '/credit-transactions',
  '/credit-packs',
  '/messaging',
  '/quotes',
  '/quote',
  '/user/choice',
  '/user/quotes',
  '/user/quote',
  '/create-quote',
  '/usable-quote-items',
  '/public/subscribe-to-plan',
  '/public/quote',
  '/club-subscriptions',
  '/create-event',
  '/events',
  '/user-pending-events',
  '/user-events',
  '/get-event',
  '/update-event-by-id',
  '/delete-event-by-id',
  '/event/seats',
  '/calendar',
  '/calendars',
  '/calendar/events',
  '/registrations',
  '/notifications',
  '/notification-settings',
  '/favorites',
  '/conversations',
  // Notes → API secondaire (route session-notes déployée uniquement sur 137jrhqqx9 ; OPTIONS y renvoie 200)
  '/session-notes',
  '/notes',
  '/animal-notes',
  '/upload-image',
  '/generate-upload-url',
  '/create-animal-profile',
  '/update-animal-profile',
  '/delete-animal',
  '/get-animals',
  '/get-animal-breeds',
  '/get-animal-species',
  '/update-service-address',
  '/search-client-by-email',
  '/documents',
  '/user-documents',
  '/library',
  '/client-segments',
  // emails branding / journal / test → API secondaire (primaire saturée API Gateway)
  '/pro-email-templates',
  '/pro-email-branding',
  '/pro-outbound-emails',
  '/pro-messaging-automations',
  // boarding → API secondaire
  '/boarding',
  // veterinarians (animal_health_bp) → API secondaire
  '/veterinarians',
  // Routes hors quota API Gateway primaire (PutRestApi LimitExceeded)
  '/ambassador',
  // Ticket 1197 — recommandations de pros (blueprint sur l'API secondaire)
  '/pro/recommendations',
  '/follow-engine',
  '/webhooks/brevo-outbound',
  '/privacy',
  '/admin/privacy',
  '/availability-schedules',
];

// Patterns d'URL dynamiques qui doivent utiliser l'API secondaire
// Patterns d'URL dynamiques pour les routes /pro/{id}/... et /pros/{id}/...
// IMPORTANT: n'inclure que les sous-routes dont les blueprints sont enregistrés sur API_Secondary
//   forfait_controller → sold-forfaits, past-bookings, user-credits, credit-stats, credit-transactions, subscriptions
//   events_controller  → events, events-calendar
//   animal_controller  → animals, animals-with-bookings, animals-with-users
//   client_documents_controller → documents, library (folders / assignments)
// NE PAS inclure: credit-packs (payment_controller), all-clients/clients (user_controller),
//                 ics-token/calendar.ics (booking_controller) → tous sur primaire uniquement
const SECONDARY_API_REGEX: RegExp[] = [
  /^\/pro(s)?\/[^/]+\/(sold-forfaits|payment-plans|credit-pack-payment-plans|past-bookings|user-credits|credit-stats|credit-transactions|clients-for-credits|subscriptions|subscription-plans|subscription-stats|events|events-calendar|animals|animals-with-bookings|animals-with-users|documents|library|client-segments|share-stats|marketing-social-ideas)/i,
  /^\/pros?\/[^/]+\/bookings\/[^/]+\/documents\/library$/i,
  /^\/clients\/[^/]+\/(documents|segments|request-email-correction|resend-invitation)/i,
  /^\/animals(\/.*)?$/i,
  // boarding: /pros/{pro_id}/clients/{client_id}/stays → boarding_bp sur secondaire
  /^\/pros\/[^/]+\/clients\/[^/]+\/stays/i,
  // #925 quota Essentiel affiché — gateway_overflow_bp, API secondaire seulement
  /^\/pros\/[^/]+\/essentiel-plan-usage$/i,
  /^\/services\/[^/]+\/remap-forfait-services$/i,
  /^\/invoice\/[^/]+\/reject-offline-payment$/i,
];

/**
 * Routes calendrier déployées uniquement sur l'API principale (sync-status, disconnect).
 * Le reste des /calendars reste sur l'API secondaire.
 */
const CALENDAR_PRIMARY_ONLY_PATTERNS = [
  /^\/calendars\/user\/[^/]+\/sync-status$/,
  /^\/calendars\/user\/[^/]+\/disconnect$/,
];

function normalizeApiPath(endpoint: string): string {
  const cleanEndpoint = endpoint.startsWith('/api/') ? endpoint.slice(4) : endpoint;
  let pathToCheck = cleanEndpoint.startsWith('/') ? cleanEndpoint : '/' + cleanEndpoint;
  if (pathToCheck.includes('?')) {
    pathToCheck = pathToCheck.split('?')[0];
  }
  return pathToCheck;
}

/**
 * Détermine si une route doit utiliser l'API secondaire
 */
function shouldUseSecondaryApi(endpoint: string): boolean {
  // Nettoyer l'endpoint pour la comparaison (enlever /api au début si présent)
  let pathToCheck = normalizeApiPath(endpoint);

  // sync-status et disconnect → API principale uniquement (y sont déployés avec CORS)
  if (CALENDAR_PRIMARY_ONLY_PATTERNS.some(re => re.test(pathToCheck))) {
    return false;
  }

  const isLiteralMatch = SECONDARY_API_ROUTES.some(route => {
    if (pathToCheck === route) return true;
    // Exact prefix match (excluding trailing slashes to avoid false positives like pros matching pro/)
    if (pathToCheck.startsWith(route + '/')) return true;
    return false;
  });

  if (isLiteralMatch) return true;

  // Test Against Regex Patterns for dynamic routes like /pro/123/sold-forfaits
  return SECONDARY_API_REGEX.some(regex => regex.test(pathToCheck));
}

function shouldNativeUseWebProdApi(endpoint: string): boolean {
  const path = normalizeApiPath(endpoint);
  return NATIVE_FORCE_WEB_PROD_PATTERNS.some(re => re.test(path));
}

/**
 * Retourne l'URL de base de l'API appropriée pour un endpoint donné.
 */
export function getApiUrl(endpoint: string = ''): string {
  const isNative = Capacitor.isNativePlatform();
  const useSecondary = shouldUseSecondaryApi(endpoint);

  if (isNative) {
    // #424 : stats V2 → même gateway que le web (évite drift mobile_v1)
    if (shouldNativeUseWebProdApi(endpoint)) {
      return useSecondary ? API_SECONDARY_PROD : API_PRIMARY_PROD;
    }
    return useSecondary ? API_SECONDARY_MOBILE_V1 : API_PRIMARY_MOBILE_V1;
  }

  // ✅ Priorité absolue aux variables d'environnement (Netlify/Vercel)
  const envPrimaryUrl = process.env.REACT_APP_API_BASE_URL;
  const envSecondaryUrl = process.env.REACT_APP_API_SECONDARY_URL;

  if (useSecondary && envSecondaryUrl) {
    return envSecondaryUrl;
  }
  if (!useSecondary && envPrimaryUrl) {
    return envPrimaryUrl;
  }

  const isProduction = process.env.NODE_ENV === 'production';
  if (isProduction) {
    return useSecondary ? API_SECONDARY_PROD : API_PRIMARY_PROD;
  }

  return useSecondary ? API_SECONDARY_LOCAL : API_PRIMARY_LOCAL;
}

/**
 * Construit l'URL complète pour un endpoint
 */
/**
 * #1084 — lecture publique du blog (liste / catégories).
 * Sur staging, REACT_APP_API_BASE_URL pointe vers une API sans articles blog :
 * Chrome hydrate alors /blog/ en « Il n'y a pas d'article… », alors que Googlebot
 * (seo-bot) lit l'API prod (w6khpitahj) et voit les articles + le bloc orphelins.
 * On aligne le SPA sur la même base que seo-bot, sauf override explicite.
 */
export function getBlogApiBaseUrl(): string {
  const override =
    process.env.REACT_APP_BLOG_API_BASE_URL || process.env.REACT_APP_API_URL;
  if (override && override.trim()) {
    return override.replace(/\/+$/, '');
  }
  if (Capacitor.isNativePlatform()) {
    return API_PRIMARY_MOBILE_V1;
  }
  if (process.env.NODE_ENV === 'production') {
    return API_PRIMARY_PROD;
  }
  return getPrimaryBaseUrl().replace(/\/+$/, '');
}

export function buildBlogApiUrl(endpoint: string): string {
  const base = getBlogApiBaseUrl();
  const clean = endpoint.replace(/^\/+/, '');
  return `${base}/${clean}`;
}

export function buildApiUrl(endpoint: string): string {
  const baseUrl = getApiUrl(endpoint).replace(/\/+$/, '');
  const cleanEndpoint = endpoint.replace(/^\/+/, '');
  return `${baseUrl}/${cleanEndpoint}`;
}

/** URL de base de l’API principale (celle utilisée pour /health, auth, etc.) */
export function getPrimaryBaseUrl(): string {
  const isNative = Capacitor.isNativePlatform();
  if (isNative) {
    return API_PRIMARY_MOBILE_V1;
  }

  const envPrimaryUrl = process.env.REACT_APP_API_BASE_URL;
  if (envPrimaryUrl) return envPrimaryUrl;

  const isProduction = process.env.NODE_ENV === 'production';
  if (isProduction) return API_PRIMARY_PROD;

  return API_PRIMARY_LOCAL;
}

/** URL de base de l’API secondaire (forfaits, events, etc.) */
export function getSecondaryBaseUrl(): string {
  return getApiUrl('/forfaits');
}

const LOCALHOST_API_RE = /^https?:\/\/(localhost|127\.0\.0\.1):800[01]\/?/;

/**
 * Réécrit une URL de requête sur app native : localhost ou chemin relatif → API mobile_v1.
 */
export function rewriteNativeRequestUrl(url: string): string {
  if (!Capacitor.isNativePlatform()) {
    return url;
  }

  if (LOCALHOST_API_RE.test(url)) {
    const path = url.replace(LOCALHOST_API_RE, '') || '/';
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const base = getApiUrl(cleanPath).replace(/\/+$/, '');
    return `${base}${cleanPath}`;
  }

  if (!url.startsWith('http')) {
    let cleanPath = url;
    if (cleanPath.startsWith('/api/')) {
      cleanPath = cleanPath.substring(4);
    } else if (cleanPath === '/api') {
      cleanPath = '/';
    }
    if (cleanPath && !cleanPath.startsWith('/')) {
      cleanPath = `/${cleanPath}`;
    }
    const base = getApiUrl(cleanPath || '/').replace(/\/+$/, '');
    return `${base}${cleanPath}`;
  }

  return url;
}
